/**
 * Decode JWT token to extract user and tenant information
 * @param {string} token - JWT token
 * @returns {object|null} - Decoded token payload or null if invalid
 */
export const decodeJWTToken = (token) => {
  try {
    if (!token) return null;
    
    // Remove 'Bearer ' prefix if present
    const cleanToken = token.replace('Bearer ', '');
    
    // JWT tokens have 3 parts separated by dots
    const parts = cleanToken.split('.');
    if (parts.length !== 3) return null;
    
    // Decode the payload (second part)
    const payload = parts[1];
    
    // Add padding if needed for base64 decoding
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    
    // Decode base64
    const decodedPayload = atob(paddedPayload);
    
    // Parse JSON
    return JSON.parse(decodedPayload);
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
};

/**
 * Get current user information from stored JWT token
 * @returns {object|null} - User information including schema_name
 */
export const getCurrentUserInfo = () => {
  // Try multiple possible token keys in order of preference
  const token = localStorage.getItem('auth_token') || 
                localStorage.getItem('authToken') ||
                localStorage.getItem('token') ||
                localStorage.getItem('access_token');
                
  if (!token) {
    return null;
  }
  
  const decoded = decodeJWTToken(token);
  if (!decoded) {
    return null;
  }
  
  return {
    userId: decoded.sub,
    email: decoded.email,
    clinicId: decoded.clinic_id,
    schemaName: decoded.schema_name, // This should be 'ksl_clinic_5' for KSL
    role: decoded.role,
    exp: decoded.exp,
    // Include the raw decoded object for debugging
    _raw: decoded
  };
};

/**
 * Check if the current token is expired
 * @returns {boolean} - True if token is expired
 */
export const isTokenExpired = () => {
  const userInfo = getCurrentUserInfo();
  if (!userInfo || !userInfo.exp) return true;
  
  // Check if token is expired (exp is in seconds, Date.now() is in milliseconds)
  return Date.now() >= userInfo.exp * 1000;
};

/**
 * Generate a tenant-specific cache key
 * @param {string} baseKey - Base cache key
 * @param {string} schemaName - Optional schema name (will get from token if not provided)
 * @returns {string} - Tenant-specific cache key
 */
export const getTenantCacheKey = (baseKey, schemaName = null) => {
  const schema = schemaName || getCurrentUserInfo()?.schemaName;
  if (!schema) {
    console.warn('No schema name available for cache key, using base key only');
    return baseKey;
  }
  
  return `${baseKey}_${schema}`;
}; 